import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

/**
 * Standalone, reusable modal editor for a user's `custom:claims` value —
 * a JSON array of per-scanner permission objects. The shape of each
 * entry is driven by the owning customer's `claims_schema` metadata
 * (F2-ADMIN.Customers.claims_schema), so the editor adapts to whichever
 * customer's scanner the entry is for.
 *
 *   <app-claims-editor
 *     *ngIf="showClaimsEditor"
 *     [customers]="customers"
 *     [scanners]="scanners"
 *     [userScanners]="user.attributes['custom:scanners']"
 *     [claimsJson]="user.attributes['custom:claims']"
 *     [title]="'Edit Claims — ' + user.email"
 *     (save)="onSaveClaims($event)"
 *     (close)="showClaimsEditor = false"
 *   ></app-claims-editor>
 *
 * The dialog renders ONE collapsible card per SCANNER the user is
 * entitled to. Entitlement comes from the user's `custom:scanners`
 * attribute — wildcards like `tt-*` are expanded against the actual
 * Scanners list (per the wildcard contract). Each card maps the scanner
 * to its owning customer (Scanner.Client) and uses that customer's
 * `claims_schema` to render the editor. Each card's "enable" checkbox
 * doubles as the expand/collapse trigger:
 *   unchecked → no claim entry persists for this scanner
 *   checked   → card expands, shows schema-driven editor
 * Orphan entries (claims targeting scanners the user is no longer
 * entitled to, or scanners whose customer has no schema) render as a
 * tail "raw" section so the admin can review + prune them rather than
 * silently losing data.
 *
 * `save` emits the canonical JSON string. `close` fires when the admin cancels.
 */

export interface SchemaField {
  key: string;
  label: string;
  type: 'int' | 'string' | 'bool' | 'stringbool' | 'array';
  default?: any;
  required?: boolean;
  enum?: { value: any; label: string }[];   // labeled options. For type='int'/'string' = single-select. For type='array' = multi-select with stored value/display label.
  options?: string[];                         // simple string allowlist when type === 'array' (legacy — prefer `enum` for new schemas so display + storage can differ).
  description?: string;
}

export interface ClaimsSchema {
  multiple_entries_per_scanner: boolean;
  template: any;
  fields: SchemaField[];
}

interface CustomerLite {
  _id: string;
  name?: string;
  scanner_ids?: string[];
  claims_schema?: ClaimsSchema;
}

interface ScannerLite {
  _id?: string;
  Id?: string;
  Name?: string;
  Client?: string;
  // Per-scanner claims schema. Mike 2026-06-28: source of truth flipped
  // from Customer.claims_schema to Scanners.<id>.claims_schema so multi-
  // scanner customers can diverge schemas. Editor prefers this; falls
  // back to Customer.claims_schema during transition until migration
  // completes and the customer-level field can be retired.
  claims_schema?: ClaimsSchema;
}

interface EntryRow {
  raw: any;                   // the actual claim entry object (mutated in place; `any` so [(ngModel)]="row.raw[f.key]" passes strict template checks)
}

interface OrphanRow {
  customerSlug: string;       // resolved slug or '' when unknown
  raw: any;
  fallbackJson: string;
  fallbackError?: string;
}

interface ClaimsCard {
  scannerId: string;          // entry.scanner — single source of truth for this card
  scannerName: string;        // display label (Scanner.Name) or '' if unknown
  customer: CustomerLite;     // owning customer (Scanner.Client → Customers._id), used for the schema
  schema: ClaimsSchema;       // === customer.claims_schema (non-null by construction)
  enabled: boolean;           // checkbox state; when off, entry is NOT saved
  entry: EntryRow;            // exactly one entry per scanner — Mike 2026-06-28 contract flip from per-customer to per-scanner
}

@Component({
  standalone: true,
  selector: 'app-claims-editor',
  imports: [CommonModule, FormsModule],
  templateUrl: './claims-editor.component.html',
})
export class ClaimsEditorComponent implements OnChanges {
  @Input() customers: CustomerLite[] = [];
  @Input() scanners: ScannerLite[] = [];
  // Comma-separated scanner-id list from the user's custom:scanners
  // attribute. Drives which scanner cards render. Wildcards like 'tt-*'
  // are expanded against the Scanners list (per
  // feedback_custom_scanners_wildcard_contract.md). Empty string = no
  // entitlement = no cards (orphan section still renders existing claim
  // entries so they aren't silently dropped on save).
  @Input() userScanners: string = '';
  // Legacy input — pre-2026-06-28 the editor was driven by the user's
  // custom:customers list. Now driven by custom:scanners. Kept for
  // backward compat with callers still passing it; ignored by the
  // card-building logic. Remove once all callers migrate.
  @Input() userCustomers: string = '';
  // Target user's custom:role — when 'admin', the entitlement filter
  // (based on the user's custom:scanners CSV) is bypassed and every
  // scanner in the catalog renders as a card. Admin-role users often
  // have empty or wildcard custom:scanners and still need to author
  // claims fleet-wide. Mike 2026-07-06.
  @Input() userRole: string = '';
  @Input() claimsJson: string = '';
  @Input() title: string = 'Edit Claims';
  // Display-only — header renders these as "name" (bold) + "email"
  // (muted) under the dialog title. Hosts pass the user's full name
  // and email pulled from the Cognito attributes so the editor doesn't
  // need to know how to derive them. Mike 2026-06-26: "the user number
  // at the top should be replaced with name; sub text email."
  @Input() userDisplayName: string = '';
  @Input() userEmail: string = '';
  // IT-F2-106 Phase 3b — flat/inline mode: render just the card body
  // (skip the fixed overlay, title bar, cancel/save buttons). Used by
  // the Add-User drawer to embed a per-scanner Claims sub-panel inline
  // under each enabled scanner row instead of round-tripping through
  // a modal. When true, the per-card "enable" checkbox is HIDDEN and
  // forced to true — the parent's scanner-enable state IS the gate.
  // Auto-emits `save` on every field change so the parent can capture
  // the latest JSON without an explicit Save click.
  @Input() flat: boolean = false;

  @Output() save = new EventEmitter<string>();
  @Output() close = new EventEmitter<void>();

  cards: ClaimsCard[] = [];
  // Same cards re-shaped as one bucket per customer for the template's
  // outer "customer box" loop. Built in _parse_claims so the template
  // iterates a stable reference (no per-tick recompute → no CD loop).
  cardGroups: { customer: CustomerLite; cards: ClaimsCard[] }[] = [];
  orphans: OrphanRow[] = [];
  parseError: string | null = null;

  // Quick lookup maps built on changes.
  private _scannerToCustomer = new Map<string, string>();
  private _customerById = new Map<string, CustomerLite>();

  // IT-F2-106 Phase 3b hardening: memoize the last-parsed input signature
  // so re-parses only run when @Inputs actually change. Angular's
  // ngOnChanges CAN fire spuriously — and when it does, rebuilding
  // cards/entries mid-CD-cycle risks a re-entry loop between the
  // ngModel bindings and the auto-emit hooks. Cheap string-key skip
  // saves us from that + drops needless allocations.
  private _lastParsedKey: string | null = null;

  ngOnChanges(_: SimpleChanges) {
    this._rebuild_lookups();
    // Signature that captures every input _parse_claims consumes.
    // Reference-cheap since scanners/customers are stable arrays and
    // strings are primitive. If nothing changed, we skip the rebuild
    // (and the ngModel rebind it would trigger).
    const key = JSON.stringify([
      this.userScanners || '',
      this.userRole || '',
      this.claimsJson || '',
      this.flat,
      (this.scanners || []).length,
      (this.customers || []).length,
    ]);
    if (key !== this._lastParsedKey) {
      this._parse_claims();
      this._lastParsedKey = key;
    }
    // Flat/inline mode: force every card enabled so the parent's outer
    // scanner-enable checkbox is the only gate.
    if (this.flat) {
      for (const c of this.cards) c.enabled = true;
    }
  }

  /**
   * IT-F2-106 Phase 3b: auto-emit save in flat mode. Called by every
   * value-change handler (onStringBoolChange, toggleArrayValue,
   * onGroupSelectChange, and ngModelChange on scalar fields).
   *
   * Re-entry guard: if a synchronous _emitIfFlat chain is already in
   * flight (e.g. an ngModel init callback fires during our own
   * dispatched change detection), skip. Prevents the freeze from
   * IT-F2-106 Phase 3b on as-os (Alpha Shark Option Sniper) which
   * has a grouped-array claims_schema whose initial ngModel binds
   * were re-triggering the emit → parent-Map-update → child-input-
   * change → re-parse → re-bind chain.
   */
  private _emitting = false;
  _emitIfFlat() {
    if (!this.flat) return;
    if (this._emitting) return;
    this._emitting = true;
    try {
      this.onSave();
    } finally {
      // Release on the next microtask so a real user-driven emit still
      // gets through, but any synchronous re-entry inside the same
      // dispatch cycle is blocked.
      Promise.resolve().then(() => { this._emitting = false; });
    }
  }

  private _rebuild_lookups() {
    this._scannerToCustomer.clear();
    this._customerById.clear();
    for (const c of (this.customers || [])) {
      this._customerById.set(c._id, c);
      for (const sid of (c.scanner_ids || [])) this._scannerToCustomer.set(sid, c._id);
    }
    // Belt-and-braces: also walk the Scanners list so entries whose
    // scanner isn't in the customer's scanner_ids[] (a common drift)
    // still get a customer mapping via Scanner.Client.
    for (const s of (this.scanners || [])) {
      const id = String(s._id || s.Id || '');
      const client = String(s.Client || '');
      if (id && client && !this._scannerToCustomer.has(id)) {
        this._scannerToCustomer.set(id, client);
      }
    }
  }

  // Tokens from custom:scanners, trimmed + deduped. May contain literal
  // ids ('as-os') AND/OR wildcards ('tt-*'). Both are honored.
  private _entitledTokens(): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const s of String(this.userScanners || '').split(',')) {
      const t = s.trim();
      if (t && !seen.has(t)) { seen.add(t); out.push(t); }
    }
    return out;
  }

  // Expand entitlement tokens against the actual Scanners list. Literal
  // ids match exact; wildcards ('tt-*') match by prefix per the standing
  // wildcard contract. Returns deduped scanner ids in display order
  // (Scanners list order preserved).
  private _isAdminTarget(): boolean {
    return String(this.userRole || '').trim().toLowerCase() === 'admin';
  }

  private _entitledScannerIds(): string[] {
    // Admin-role targets see every scanner regardless of their
    // custom:scanners CSV — their role grants fleet-wide access.
    if (this._isAdminTarget()) {
      const all = new Set<string>();
      for (const s of (this.scanners || [])) {
        const id = String(s._id || s.Id || '');
        if (id) all.add(id);
      }
      return Array.from(all);
    }
    const tokens = this._entitledTokens();
    if (tokens.length === 0) return [];
    const literals = new Set<string>();
    const prefixes: string[] = [];
    for (const t of tokens) {
      if (t.endsWith('*')) prefixes.push(t.slice(0, -1));
      else literals.add(t);
    }
    const matched = new Set<string>();
    for (const s of (this.scanners || [])) {
      const id = String(s._id || s.Id || '');
      if (!id) continue;
      if (literals.has(id)) { matched.add(id); continue; }
      for (const p of prefixes) {
        if (id.startsWith(p)) { matched.add(id); break; }
      }
    }
    // Also keep literals that don't appear in the Scanners list — admin
    // may be onboarding a scanner that hasn't landed in F2-ADMIN yet, or
    // there's a typo to surface. Render them as a card with a missing-
    // scanner badge so the admin sees the discrepancy.
    for (const l of literals) matched.add(l);
    return Array.from(matched);
  }

  private _parse_claims() {
    this.cards = [];
    this.orphans = [];
    this.parseError = null;

    // 1. Build a card per entitled SCANNER (Mike 2026-06-28 contract:
    //    one settings record per scanner, visible because of the user's
    //    custom:scanners value). Pre-seed each card with a fresh
    //    template-based entry; the `enabled` flag decides whether it's
    //    written on save.
    const scannerById = new Map<string, ScannerLite>();
    for (const s of (this.scanners || [])) {
      const id = String(s._id || s.Id || '');
      if (id) scannerById.set(id, s);
    }
    const cardByScanner = new Map<string, ClaimsCard>();
    for (const scannerId of this._entitledScannerIds()) {
      const scanner = scannerById.get(scannerId);
      const customerSlug = this._scannerToCustomer.get(scannerId) || '';
      // Non-admin callers (client / client_admin) can't read the full
      // customer-directory (super-admin only), so `customers` arrives
      // empty and _customerById.get() misses. When the Scanner already
      // carries its own claims_schema we don't NEED the customer doc —
      // synthesize a minimal stub `{_id, name}` from the slug so the
      // per-scanner card still renders. Downstream code only reads
      // `.name` / `._id` off the customer for display + grouping.
      // Mike 2026-07-02: alpha-shark client operators were seeing the
      // "not entitled" empty state even for as-os which has a valid
      // Scanner.claims_schema.
      const customer = customerSlug
        ? (this._customerById.get(customerSlug) || { _id: customerSlug, name: customerSlug } as CustomerLite)
        : undefined;
      // Source of truth is Scanner.claims_schema (Mike 2026-06-28).
      // Fall back to Customer.claims_schema ONLY when the customer's
      // schema doesn't pin a different scanner via template.scanner —
      // otherwise multi-scanner customers (alpha-shark owns as-os +
      // alpha-shark-flow + as-dash; the customer schema pins as-os)
      // would have their as-os schema bleed onto the sibling scanners.
      // Unpinned customer schemas (template.scanner empty/missing) are
      // legitimately shared across the customer's scanner family
      // (theo-trade, mta, option-pit) so the fallback still fires.
      const scannerSchema = scanner?.claims_schema;
      const custSchema = (customer as any)?.claims_schema;
      const custTemplateScanner = custSchema?.template?.scanner || '';
      const fallbackOk = !!custSchema && (!custTemplateScanner || custTemplateScanner === scannerId);
      const schema = scannerSchema || (fallbackOk ? custSchema : null);
      if (!customer || !schema) continue;  // no schema → nothing to edit
      const card: ClaimsCard = {
        scannerId,
        scannerName: String(scanner?.Name || ''),
        customer,
        schema,
        enabled: false,
        entry: this._fresh_entry_for_scanner(scannerId, schema),
      };
      this.cards.push(card);
      cardByScanner.set(scannerId, card);
    }
    // Stable display order: by customer name, then scanner id within.
    this.cards.sort((a, b) => {
      const an = String(a.customer.name || a.customer._id).toLowerCase();
      const bn = String(b.customer.name || b.customer._id).toLowerCase();
      const c = an.localeCompare(bn);
      return c !== 0 ? c : a.scannerId.localeCompare(b.scannerId);
    });

    // Bucket into per-customer groups for the template's outer box.
    // Preserves the cards-sorted order so customers appear in name order
    // and scanners under each customer appear in scanner-id order.
    this.cardGroups = [];
    let currentSlug = '';
    let currentBucket: { customer: CustomerLite; cards: ClaimsCard[] } | null = null;
    for (const c of this.cards) {
      if (c.customer._id !== currentSlug) {
        currentSlug = c.customer._id;
        currentBucket = { customer: c.customer, cards: [] };
        this.cardGroups.push(currentBucket);
      }
      currentBucket!.cards.push(c);
    }

    // 2. Parse existing claims JSON and distribute entries.
    const raw = (this.claimsJson || '').trim();
    if (!raw) return;
    let arr: any;
    try { arr = JSON.parse(raw); } catch (e: any) {
      this.parseError = 'Existing claims value is not valid JSON: ' + (e?.message || 'parse error');
      return;
    }
    if (!Array.isArray(arr)) {
      this.parseError = 'Existing claims value is not a JSON array.';
      return;
    }

    // Track which cards we've already hydrated so duplicate entries
    // (two entries for the same scanner — shouldn't happen, but legacy
    // data may have it) fall to orphans for the admin to clean up
    // rather than silently overwriting the first.
    const hydrated = new Set<string>();
    for (const entry of arr) {
      const obj = (entry && typeof entry === 'object') ? entry : { scanner: '' };
      const sc = String(obj.scanner || '');
      const card = sc ? cardByScanner.get(sc) : undefined;
      if (card && !hydrated.has(sc)) {
        card.entry = { raw: { ...obj } };
        card.enabled = true;
        hydrated.add(sc);
      } else {
        // Orphan: scanner unknown / user not entitled to it / second
        // entry for an already-hydrated scanner. Surface to the admin.
        const slug = this._scannerToCustomer.get(sc) || '';
        this.orphans.push({
          customerSlug: slug,
          raw: { ...obj },
          fallbackJson: JSON.stringify(obj, null, 2),
        });
      }
    }
  }

  // Toggle the "enable" checkbox on a card. Card.entry is pre-seeded at
  // parse time so we just flip the enabled flag — save() emits the entry
  // iff enabled. When toggling OFF we leave entry contents in memory so
  // admin can toggle back without losing work.
  onToggleCard(card: ClaimsCard, value: boolean) {
    card.enabled = value;
  }

  // Header click also toggles when admin clicks anywhere on the card header.
  onHeaderToggle(card: ClaimsCard) {
    this.onToggleCard(card, !card.enabled);
  }

  private _fresh_entry_for_scanner(scannerId: string, schema: ClaimsSchema): EntryRow {
    const tmpl = JSON.parse(JSON.stringify(schema.template || { scanner: '' }));
    tmpl.scanner = scannerId;  // card-scoped scanner — never picker-driven anymore
    return { raw: tmpl };
  }

  // Orphan removal: explicit prune by the admin.
  onRemoveOrphan(idx: number) {
    this.orphans.splice(idx, 1);
  }

  // Boolean-as-string ("true" / "false") coercion for stringbool fields.
  onStringBoolChange(row: EntryRow, field: SchemaField, value: any) {
    row.raw[field.key] = String(value);
    this._emitIfFlat();
  }

  // Resolves the option set for a stringbool field. Pulled out of the
  // template because the previous inline array literal in *ngFor tripped
  // Vercel's stricter Angular template type-checker.
  stringBoolOptions(field: SchemaField): { value: string; label: string }[] {
    if (field.enum && field.enum.length) return field.enum as any;
    return [{ value: 'true', label: 'true' }, { value: 'false', label: 'false' }];
  }

  // Array toggle — for fields whose schema has an `options[]` allowlist.
  // Backward-compat: if the stored value is still a legacy single string
  // (e.g. claims_schema flipped from `type:'string'+enum` to `type:'array'+
  // options` without migrating old user attributes), treat that string as
  // a 1-element array on first toggle.
  private _asArray(v: any): any[] {
    if (Array.isArray(v)) return v;
    if (v !== undefined && v !== null && v !== '') return [v];
    return [];
  }
  toggleArrayValue(row: EntryRow, field: SchemaField, value: any) {
    let cur = this._asArray(row.raw[field.key]);
    if (cur.some((x: any) => x === value)) cur = cur.filter((x: any) => x !== value);
    else cur = [...cur, value];
    row.raw[field.key] = cur;
    this._emitIfFlat();
  }
  isArrayValueSelected(row: EntryRow, field: SchemaField, value: any): boolean {
    return this._asArray(row.raw[field.key]).some((x: any) => x === value);
  }
  // String-only aliases kept for backward-compat with any external caller.
  toggleArrayOption(row: EntryRow, field: SchemaField, option: string) {
    this.toggleArrayValue(row, field, option);
  }
  isArrayOptionSelected(row: EntryRow, field: SchemaField, option: string): boolean {
    return this.isArrayValueSelected(row, field, option);
  }

  // Grouped-mode renderer for array+enum fields whose labels follow a
  // "Subscanner - Mode" convention (e.g. "Moonshot - Free (no live)",
  // "Moonshot - PRS (live + rank)"). The flat checkbox grid lets the
  // admin pick multiple tiers for the same subscanner, which is never
  // the intent — tiers are mutually exclusive. Auto-detect: when every
  // enum label is splittable on " - " into a 2-part [group, mode]
  // pair AND there are at least two distinct groups, render one
  // dropdown per group (Disabled + per-mode options); fall back to
  // the flat checkbox grid otherwise.
  //
  // Storage shape stays unchanged: the saved array contains the picked
  // raw enum values, and "Disabled" stores no value for that group.
  // Existing user records continue to round-trip cleanly.
  //
  // Memoization: the template binds *ngFor + isGroupedArrayField in
  // CD-tick hot paths. Returning a fresh array per call triggers
  // *ngFor's identity diff → re-render → another tick → freeze. Cache
  // by SchemaField identity so the same call returns the same array
  // reference until the schema changes.
  private static readonly _GROUP_SEPARATOR = ' - ';
  private _isGroupedCache = new WeakMap<SchemaField, boolean>();
  private _groupsCache = new WeakMap<SchemaField, { group: string; options: { value: any; modeLabel: string }[] }[]>();

  isGroupedArrayField(field: SchemaField): boolean {
    if (!field) return false;
    const cached = this._isGroupedCache.get(field);
    if (cached !== undefined) return cached;
    const computed = this._computeIsGrouped(field);
    this._isGroupedCache.set(field, computed);
    return computed;
  }

  private _computeIsGrouped(field: SchemaField): boolean {
    if (field.type !== 'array' || !field.enum || field.enum.length < 2) return false;
    const groups = new Set<string>();
    for (const o of field.enum) {
      const label = String(o.label ?? '');
      const idx = label.indexOf(ClaimsEditorComponent._GROUP_SEPARATOR);
      if (idx <= 0 || idx >= label.length - ClaimsEditorComponent._GROUP_SEPARATOR.length) return false;
      groups.add(label.slice(0, idx).trim());
    }
    return groups.size >= 2;
  }

  arrayFieldGroups(field: SchemaField): { group: string; options: { value: any; modeLabel: string }[] }[] {
    const cached = this._groupsCache.get(field);
    if (cached) return cached;
    const out: { group: string; options: { value: any; modeLabel: string }[] }[] = [];
    const byGroup = new Map<string, { value: any; modeLabel: string }[]>();
    const order: string[] = [];
    for (const o of (field.enum || [])) {
      const label = String(o.label ?? '');
      const idx = label.indexOf(ClaimsEditorComponent._GROUP_SEPARATOR);
      const group = label.slice(0, idx).trim();
      const modeLabel = label.slice(idx + ClaimsEditorComponent._GROUP_SEPARATOR.length).trim();
      if (!byGroup.has(group)) { byGroup.set(group, []); order.push(group); }
      byGroup.get(group)!.push({ value: o.value, modeLabel });
    }
    for (const g of order) out.push({ group: g, options: byGroup.get(g)! });
    this._groupsCache.set(field, out);
    return out;
  }

  getGroupSelectedValue(row: EntryRow, field: SchemaField, groupOptions: { value: any; modeLabel: string }[]): any {
    const cur = this._asArray(row.raw[field.key]);
    const valueSet = new Set(groupOptions.map(o => o.value));
    for (const v of cur) if (valueSet.has(v)) return v;
    return '';
  }

  onGroupSelectChange(row: EntryRow, field: SchemaField, groupOptions: { value: any; modeLabel: string }[], picked: any) {
    const valueSet = new Set(groupOptions.map(o => o.value));
    let cur = this._asArray(row.raw[field.key]).filter((x: any) => !valueSet.has(x));
    if (picked !== '' && picked !== null && picked !== undefined) cur = [...cur, picked];
    row.raw[field.key] = cur;
    this._emitIfFlat();
  }

  // Summary line shown in collapsed card headers.
  cardSummary(card: ClaimsCard): string {
    if (!card.enabled) return '';
    return 'enabled';
  }

  // Header subtitle — customer name + scanner id so the admin can tell
  // multiple cards from the same customer apart (mta has 4 scanners
  // sharing one schema → 4 cards differing only by scanner id).
  cardSubtitle(card: ClaimsCard): string {
    const cname = String(card.customer.name || card.customer._id);
    return `${cname} • ${card.scannerId}`;
  }

  onSave() {
    const out: any[] = [];
    for (const card of this.cards) {
      if (!card.enabled) continue;
      out.push(this._coerce_entry(card.entry, card.schema));
    }
    // Orphans (no schema or no entitlement) — re-parse their fallback JSON
    // so any edits the admin made stick.
    for (const o of this.orphans) {
      try {
        out.push(JSON.parse(o.fallbackJson));
        o.fallbackError = undefined;
      } catch (e: any) {
        o.fallbackError = 'Invalid JSON: ' + (e?.message || 'parse error');
        return;
      }
    }
    this.save.emit(JSON.stringify(out));
  }

  private _coerce_entry(row: EntryRow, schema: ClaimsSchema): any {
    const out: any = { scanner: row.raw.scanner };
    for (const f of (schema?.fields || [])) {
      const v = row.raw[f.key];
      if (v === undefined || v === null) {
        if (f.default !== undefined) out[f.key] = f.default;
        continue;
      }
      switch (f.type) {
        case 'int':         out[f.key] = Number.isFinite(Number(v)) ? Number(v) : (f.default ?? 0); break;
        case 'bool':        out[f.key] = !!v; break;
        case 'stringbool':  out[f.key] = String(v); break;
        case 'array':       out[f.key] = Array.isArray(v) ? v : []; break;
        case 'string':
        default:            out[f.key] = String(v); break;
      }
    }
    // Preserve extra keys present on the raw entry that the schema doesn't
    // describe — defensive for older entries with deprecated fields.
    for (const k of Object.keys(row.raw)) {
      if (k === 'scanner' || k in out) continue;
      out[k] = row.raw[k];
    }
    return out;
  }

  onCancel() {
    this.close.emit();
  }
}
